import type { PasswordResetConfirmInput, PasswordResetRequestInput } from '@lib/input/tenant-auth.input'
import { api, publicApi } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  loginResponseSchema,
  userSchema,
  type LoginInput,
  type LoginResponse,
  type User,
} from '@/schemas/auth.schema'
import type { IAuthService } from '@/types/AuthService.types'

/**
 * Service for authentication endpoints. Every method validates its response
 * through the `api` client's schema boundary, so the returned values are typed
 * by the schemas, not by unchecked assertions.
 */
class AuthService implements IAuthService {
  /**
   * Log in with email and password.
   * @param input Login credentials.
   * @returns The access token and the signed-in user.
   */
  async login(input: LoginInput): Promise<LoginResponse> {
    try {
      return await api.post('/auth/login', input, loginResponseSchema)
    } catch (error) {
      logger.error('Login failed', { error: toErrorMessage(error) })
      throw error
    }
  }

  /**
   * Fetch the currently authenticated user.
   * @returns The signed-in user.
   */
  async me(): Promise<User> {
    try {
      return await api.get('/auth/me', userSchema)
    } catch (error) {
      logger.error('Failed to load current user', { error: toErrorMessage(error) })
      throw error
    }
  }

  async logout(): Promise<void> {
    await api.postEmpty('/auth/logout')
  }

  /** Emails a reset link if the address is a staff account (the response never says). */
  async requestPasswordReset(input: PasswordResetRequestInput): Promise<void> {
    await publicApi.postEmpty('/auth/password-reset/request', input)
  }

  /** Sets a new password from an emailed link; signs the staff member out everywhere else. */
  async confirmPasswordReset(input: PasswordResetConfirmInput): Promise<void> {
    await publicApi.postEmpty('/auth/password-reset/confirm', input)
  }
}

export const authService = new AuthService()
